/**
 * V34 — MetricTooltip
 * 
 * Reusable info-icon tooltip for every metric card across all dashboards.
 * Single component → guarantees consistent UX + makes formulas discoverable.
 * 
 * Usage:
 *   <h3>
 *     Consultations Taken
 *     <MetricTooltip
 *       formula="bookings.status NOT IN {no_show, cancelled, reschedule} ∧ scheduled_at ∈ period"
 *       source="consultation_bookings"
 *       note="Replaces the unreliable lead.consultation_status flag."
 *     />
 *   </h3>
 */
import React from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export const MetricTooltip = ({ formula, source, note, testId = 'metric-tooltip' }) => {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center align-middle ml-1.5 text-slate-400 hover:text-slate-600 transition-colors"
            data-testid={testId}
            aria-label="Metric explanation"
            onClick={(e) => e.preventDefault()}
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs bg-slate-900 text-white text-xs p-3 leading-relaxed shadow-lg"
        >
          {formula && (
            <div className="mb-1.5">
              <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Formula</span>
              <div className="font-mono text-[11px] mt-0.5">{formula}</div>
            </div>
          )}
          {source && (
            <div className="mb-1.5">
              <span className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Source</span>
              <div className="font-mono text-[11px] mt-0.5">{source}</div>
            </div>
          )}
          {note && (
            <div className="text-slate-300 text-[11px] mt-1.5 italic">{note}</div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default MetricTooltip;
